/**
 * What the mobile Rate Check shows for this lane, period by period — priced at the app's own tier (RATE_CHECK_TIER, r3)
 * with the same `marketIntelPeriods` the app runs, so an operator can check the 7 and 14 days figures before switching
 * those tabs on (Mobile → Rate Check). The rest of the page answers at the finest tier with enough loads.
 *
 * Every period is shown here whatever the switches say; a chip says whether the app currently offers it.
 */

import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import { MARKET_INTEL_PERIODS, RATE_CHECK_TIER, marketIntelPeriods, type MarketIntelPeriod } from '@numeo/types-main-mobile'
import type { MarketIntelLaneQuery } from '@numeo/types-core'
import type { MobileRateCheckPeriods } from '@numeo/types'
import * as React from 'react'
import { marketIntelApi } from 'api/marketIntel'
import { mobileRateCheckApi } from 'api/mobileRateCheck'
import { ErrorState, Stat, StatRow, useLoadPipelineData } from './shared'
import { TierChip, money, totalMoney } from './marketIntel'

const LABEL: Record<MarketIntelPeriod, string> = { today: 'Today', '7d': '7 days', '14d': '14 days' }

const HOW: Record<MarketIntelPeriod, string> = {
    today: 'today’s averages, yesterday’s until today has 10 loads',
    '7d': 'averages over days 0–6, weighted by loads, as the web map',
    '14d': 'the window’s real medians',
}

/** Whether the app offers a period: Today always; the others per the Gluon switch, unknown until it is read. */
function offered(period: MarketIntelPeriod, switches: MobileRateCheckPeriods | null): boolean | null {
    if (period === 'today') return true
    if (!switches) return null
    return period === '7d' ? switches.sevenDays : switches.fourteenDays
}

export function AppPeriodsCard({ lane }: { lane: MarketIntelLaneQuery }) {
    const [period, setPeriod] = React.useState<MarketIntelPeriod>('today')
    const [switches, setSwitches] = React.useState<MobileRateCheckPeriods | null>(null)
    React.useEffect(() => {
        mobileRateCheckApi
            .get()
            .then(setSwitches)
            .catch((error) => console.error('Failed to read the mobile Rate Check periods', error))
    }, [])

    const { data, error, loading } = useLoadPipelineData(
        () => marketIntelApi.priceLanes([{ ...lane, tier: RATE_CHECK_TIER }]),
        [lane.originLat, lane.originLon, lane.destLat, lane.destLon, lane.equipment]
    )
    const periods = marketIntelPeriods(data?.lanes[0] ?? null)
    const figures = periods[period]
    const inApp = offered(period, switches)

    return (
        <Card sx={{ mt: 2 }}>
            <Typography level="title-md" endDecorator={<TierChip tier={RATE_CHECK_TIER} />}>
                What the app shows
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                The mobile Rate Checker’s figures for this lane at its r3 areas, computed exactly as the app does. The total leads and $/mi sits under it.
            </Typography>
            <Tabs value={period} onChange={(_, value) => setPeriod(value as MarketIntelPeriod)} sx={{ bgcolor: 'transparent' }}>
                <TabList tabFlex="none">
                    {MARKET_INTEL_PERIODS.map((p) => (
                        <Tab key={p} value={p}>
                            {LABEL[p]}
                        </Tab>
                    ))}
                </TabList>
            </Tabs>
            {/* A failed request is not a lane without data: show the failure, never "—". */}
            {error ? (
                <ErrorState error={error} />
            ) : (
                <StatRow>
                    <Stat label={period === '14d' ? 'Median total' : 'Average total'} value={loading ? '…' : totalMoney(figures.total)} hint={HOW[period]} />
                    <Stat
                        label={period === '14d' ? 'Median $/mi' : 'Average $/mi'}
                        value={loading ? '…' : figures.rpm == null ? '—' : `${money(figures.rpm)}/mi`}
                        hint={figures.availability.replace('_', ' ')}
                    />
                </StatRow>
            )}
            <Chip size="sm" variant="soft" color={inApp === null ? 'neutral' : inApp ? 'success' : 'warning'} sx={{ alignSelf: 'flex-start' }}>
                {inApp === null ? 'in app: unknown' : inApp ? 'shown in the app' : 'hidden in the app — switch on in Mobile → Rate Check'}
            </Chip>
        </Card>
    )
}
